import type { Column } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { Button } from '../ui/button.js';

export interface DataTableColumnHeaderProps<TData, TValue> {
  column: Column<TData, TValue>;
  title: string;
  /** Right-align for numeric columns so digits line up with the cells below. */
  align?: 'left' | 'right';
  className?: string;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  align = 'left',
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <span className={cn(align === 'right' && 'block text-right', className)}>{title}</span>;
  }

  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      // Announce the state, not just the label: "Holder, sorted ascending" tells a screen
      // reader user what pressing it will change.
      aria-label={
        sorted === 'asc'
          ? `${title}, sorted ascending`
          : sorted === 'desc'
            ? `${title}, sorted descending`
            : `${title}, not sorted`
      }
      onClick={() => column.toggleSorting(sorted === 'asc')}
      className={cn(
        '-mx-2 h-8 gap-1.5 font-medium data-[state=open]:bg-accent',
        align === 'right' && 'ml-auto flex-row-reverse',
        className,
      )}
    >
      {title}
      {sorted === 'asc' ? (
        <ArrowUpIcon className="size-3.5" aria-hidden="true" />
      ) : sorted === 'desc' ? (
        <ArrowDownIcon className="size-3.5" aria-hidden="true" />
      ) : (
        <ChevronsUpDownIcon className="size-3.5 opacity-50" aria-hidden="true" />
      )}
    </Button>
  );
}
